import { Injectable } from '@angular/core';
import type {
  PDFDocumentLoadingTask,
  PDFDocumentProxy,
  PDFPageProxy,
  RenderTask,
} from 'pdfjs-dist';

/** Dimensions d'une page, en points PDF à l'échelle 1. */
export interface PdfPageSize {
  width: number;
  height: number;
}

/** Dessin d'une page en cours, qu'on peut abandonner. */
export interface PdfRenderJob {
  promise: Promise<void>;
  cancel(): void;
}

/** Une page d'un PDF ouvert. `rotation` s'ajoute à la rotation propre de la page. */
export interface PdfPage {
  size(rotation: number): PdfPageSize;
  render(canvas: HTMLCanvasElement, scale: number, rotation: number): PdfRenderJob;
  /** Libère ce que pdf.js garde du dessin de la page ; elle reste dessinable. */
  release(): void;
}

/** Un PDF ouvert, à refermer par `destroy()` une fois affiché. */
export interface PdfFile {
  readonly pageCount: number;
  getPage(pageNumber: number): Promise<PdfPage>;
  destroy(): void;
}

class PdfJsPage implements PdfPage {
  constructor(private readonly page: PDFPageProxy) {}

  private viewport(scale: number, rotation: number) {
    return this.page.getViewport({ scale, rotation: (this.page.rotate + rotation) % 360 });
  }

  size(rotation: number): PdfPageSize {
    const { width, height } = this.viewport(1, rotation);
    return { width, height };
  }

  render(canvas: HTMLCanvasElement, scale: number, rotation: number): PdfRenderJob {
    const viewport = this.viewport(scale, rotation);
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    const task: RenderTask = this.page.render({ canvas, viewport });
    return { promise: task.promise, cancel: () => task.cancel() };
  }

  release(): void {
    this.page.cleanup();
  }
}

class PdfJsFile implements PdfFile {
  constructor(
    private readonly loadingTask: PDFDocumentLoadingTask,
    private readonly document: PDFDocumentProxy,
  ) {}

  get pageCount(): number {
    return this.document.numPages;
  }

  async getPage(pageNumber: number): Promise<PdfPage> {
    return new PdfJsPage(await this.document.getPage(pageNumber));
  }

  destroy(): void {
    void this.loadingTask.destroy();
  }
}

/**
 * Ouvre les PDF avec pdf.js, pour que l'application les dessine elle-même — et leur applique
 * l'orientation retenue — au lieu de les confier à la visionneuse du navigateur. La
 * bibliothèque n'est chargée qu'à l'ouverture du premier PDF.
 */
@Injectable({ providedIn: 'root' })
export class PdfService {
  private library?: Promise<typeof import('pdfjs-dist')>;

  private loadLibrary(): Promise<typeof import('pdfjs-dist')> {
    this.library ??= import('pdfjs-dist').then((pdfjs) => {
      pdfjs.GlobalWorkerOptions.workerPort ??= new Worker(
        new URL('./pdf.worker', import.meta.url),
        { type: 'module' },
      );
      return pdfjs;
    });
    return this.library;
  }

  async open(blob: Blob): Promise<PdfFile> {
    const pdfjs = await this.loadLibrary();
    const data = new Uint8Array(await blob.arrayBuffer());
    const loadingTask = pdfjs.getDocument({ data });
    return new PdfJsFile(loadingTask, await loadingTask.promise);
  }
}
